import {
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';

const HTTP_TOO_MANY_REQUESTS = 429;
const INITIAL_BACKOFF_MS = 500;
const MAX_RETRIES = 3;

export { MAX_RETRIES };

function getRetryAfterMs(meta?: FetchBaseQueryMeta): number | undefined {
  const retryAfterValue = meta?.response?.headers.get('Retry-After');

  if (!retryAfterValue) {
    return undefined;
  }

  const retryAfterSeconds = Number(retryAfterValue);
  if (Number.isFinite(retryAfterSeconds)) {
    return Math.max(0, retryAfterSeconds * 1000);
  }

  const retryAfterDateMs = Date.parse(retryAfterValue);
  if (Number.isNaN(retryAfterDateMs)) {
    return undefined;
  }

  return Math.max(0, retryAfterDateMs - Date.now());
}

export function is429Error(error?: FetchBaseQueryError): boolean {
  return error?.status === HTTP_TOO_MANY_REQUESTS;
}

export function getRetryDelayMs(
  meta: FetchBaseQueryMeta | undefined,
  attempt: number
): number {
  const retryAfterMs = getRetryAfterMs(meta);

  if (retryAfterMs !== undefined) {
    return retryAfterMs;
  }

  return INITIAL_BACKOFF_MS * 2 ** (attempt - 1);
}

export function waitWithAbort(
  ms: number,
  signal: AbortSignal
): Promise<boolean> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve(false);
      return;
    }

    let timer: ReturnType<typeof setTimeout>;
    const onAbort = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
      resolve(false);
    };

    timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve(true);
    }, ms);

    signal.addEventListener('abort', onAbort);
  });
}
